import type { LocationOverride, UserProfile, UserSettings } from '@/types';

export interface GeoLocation {
  city: string;
  country: string;
  latitude: number;
  longitude: number;
}

type NullableNumber = number | null | undefined;

/**
 * Calculates distance in kilometers between two lat/lng pairs using the Haversine formula.
 * Returns `null` if any coordinate is missing.
 */
export function calculateDistance(
  lat1: NullableNumber,
  lon1: NullableNumber,
  lat2: NullableNumber,
  lon2: NullableNumber
): number | null {
  if (lat1 == null || lon1 == null || lat2 == null || lon2 == null) {
    return null;
  }

  const R = 6371; // Radius of Earth in km
  const dLat = (lat2 - lat1) * (Math.PI / 180);
  const dLon = (lon2 - lon1) * (Math.PI / 180);

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * (Math.PI / 180)) *
      Math.cos(lat2 * (Math.PI / 180)) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return Math.round(R * c * 2) / 2;
}

// Fallback for missing coordinates. Sort profiles without location to the bottom.
export const UNKNOWN_DISTANCE_KM = 1e7;

export interface ViewerLocation {
  /** Coordinates the viewer browses from: the admin override when set, else the profile's public coords. */
  location: { latitude?: number; longitude?: number };
  /** True when an admin override is driving `location`. */
  isOverride: boolean;
  /** Whether distance-based discovery applies for the viewer (an override implies it, even with the toggle off). */
  locationEnabled: boolean;
}

export function resolveViewerLocation(
  profile: Pick<UserProfile, 'latitude' | 'longitude'> | null | undefined,
  settings: Pick<UserSettings, 'locationAllowed' | 'locationOverride'> | null | undefined,
  isAdmin: boolean
): ViewerLocation {
  const override = isAdmin ? settings?.locationOverride : null;
  const isOverride =
    typeof override?.latitude === 'number' && typeof override?.longitude === 'number';

  return {
    location: isOverride
      ? { latitude: override.latitude, longitude: override.longitude }
      : { latitude: profile?.latitude, longitude: profile?.longitude },
    isOverride,
    locationEnabled: isOverride || (settings?.locationAllowed ?? false),
  };
}

/** Display text for a saved override: the searched label, or the coordinates as a fallback. */
export function formatLocationOverride(override: LocationOverride | null | undefined): string {
  if (!override) return '';
  return override.label ?? `${override.latitude.toFixed(3)}, ${override.longitude.toFixed(3)}`;
}

export function getDistanceToProfile(
  current: { latitude?: number; longitude?: number } | null,
  profile: { latitude?: number; longitude?: number } | null
): number {
  if (!current || !profile) {
    return UNKNOWN_DISTANCE_KM;
  }
  return (
    calculateDistance(current.latitude, current.longitude, profile.latitude, profile.longitude) ??
    UNKNOWN_DISTANCE_KM
  );
}

export interface PlaceSearchResult {
  label: string;
  latitude: number;
  longitude: number;
}

export async function searchPlaces(query: string, limit = 5): Promise<PlaceSearchResult[]> {
  try {
    const response = await fetch(
      `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=${limit}`,
      {
        headers: {
          'Accept-Language': 'en',
          'User-Agent': 'Pulp/1.0',
        },
      }
    );

    if (!response.ok) {
      throw new Error(`Nominatim returned status ${response.status}`);
    }

    const data = (await response.json()) as Array<{
      display_name: string;
      lat: string;
      lon: string;
    }>;

    const seen = new Set<string>();
    return data
      .map(place => ({
        label: place.display_name,
        latitude: parseFloat(place.lat),
        longitude: parseFloat(place.lon),
      }))
      .filter(place => {
        if (seen.has(place.label)) return false;
        seen.add(place.label);
        return true;
      });
  } catch (err) {
    console.error('Place search failed:', err);
    return [];
  }
}

export async function getCityCoordinates(
  city: string
): Promise<{ latitude: number; longitude: number } | null> {
  const [place] = await searchPlaces(city, 1);
  return place ? { latitude: place.latitude, longitude: place.longitude } : null;
}
